import React, {
  useContext,
  useEffect,
  useReducer,
  useState,
  useRef,
} from 'react';
import api from '../api';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { toast } from 'react-toastify';
import { Store } from '../Store';
import MessageBox from '../components/general/MessageBox';
import { getError } from '../utils';
import Card from 'react-bootstrap/Card';
import { Container, Nav } from 'react-bootstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Dropdown,
  DropdownButton,
  Modal,
  Button,
  Badge,
  Form,
} from 'react-bootstrap';
import Pagination from '../components/general/Pagination';
import LoadingLogo from '../components/general/LoadingLogo';
import { useTranslation } from 'react-i18next';
import {
  designsWithThumbnails,
  findSoldOutLayouts,
  isDesignSoldOut,
  ownsDesign,
  publicStatusChange,
  purchaseDisabled,
  renameCheck,
  savedDesignCartItem,
} from '../utils/savedDesigns';
import { productNameOfLayout } from '../utils/customizerLayout';
import { useInFlightGuard } from '../utils/useInFlightGuard';

const reducer = (state, action) => {
  switch (action.type) {
    case 'FETCH_REQUEST':
      return { ...state, loading: true, error: '' }; // a kept error would outlive the next successful fetch (the render checks it after loading)
    case 'FETCH_LAYOUTS_REQUEST':
      return { ...state, loadingLayouts: true };
    case 'FETCH_SUCCESS':
      return {
        ...state,
        designs: action.payload.designs,
        page: action.payload.page,
        pages: action.payload.pages,
        loading: false,
      };
    case 'FETCH_LAYOUTS_SUCCESS': {
      // Create a Set of sold-out profiles for each layout
      const soldOutLayouts = findSoldOutLayouts(action.payload.layouts);

      // Attach soldOutLayouts to the state for efficient checks
      return {
        ...state,
        layouts: action.payload.layouts,
        products: action.payload.products,
        soldOutLayouts,
        loadingLayouts: false,
      };
    }
    case 'FETCH_FAIL':
      return { ...state, loading: false, error: action.payload };
    case 'FETCH_LAYOUTS_FAIL':
      return { ...state, loadingLayouts: false, error: action.payload };

    case 'UPDATE_DESIGN_NAME':
      return {
        ...state,
        designs: action.payload.designs,
        loading: false,
      };

    case 'UPDATE_PUBLIC_STATUS':
      return {
        ...state,
        designs: state.designs.map((design) =>
          design._id === action.payload.id
            ? {
                ...design,
                metadata: {
                  ...design.metadata,
                  // Update publicStatus only if it exists in the payload
                  ...(action.payload.publicStatus !== undefined && {
                    publicStatus: action.payload.publicStatus,
                  }),
                },
              }
            : design
        ),
      };

    default:
      return state;
  }
};

// a design's thumbnail, wrapped in a button while it can be enlarged so the zoom is reachable by keyboard too (the button takes its accessible name from the image's alt text):
function DesignThumbnail({ design, layoutMode, isZoomable, onZoom }) {
  const thumbnail = (
    <img
      src={design.metadata.signedThumbnailUrl}
      alt={design.name}
      className={
        layoutMode === 1
          ? 'img-fluid'
          : 'img-fluid thumbnail-community-designs-scale'
      }
    />
  );

  if (!isZoomable) {
    return thumbnail;
  }

  return (
    <button
      type="button"
      className="a11y-unstyled-button"
      onClick={() => onZoom(design.metadata.signedThumbnailUrl)}
    >
      {thumbnail}
    </button>
  );
}

export default function MyDesignsScreen() {
  const [
    {
      loading,
      loadingLayouts,
      error,
      designs,
      pages,
      layouts,
      products,
      soldOutLayouts,
    },
    dispatch,
  ] = useReducer(reducer, {
    loading: true,
    loadingLayouts: true,
    error: '',
    designs: [],
    layouts: [],
    soldOutLayouts: new Set(),
  });

  const { t } = useTranslation();
  const { state, dispatch: ctxDispatch } = useContext(Store);
  const { userInfo } = state; // gets the userInfo out of the application state, so that the users token can be used to authorize the fetch request
  const navigate = useNavigate();
  const inputRef = useRef(null); // a ref for the input field

  const [editingId, setEditingId] = useState(null);
  const [name, setName] = useState('');
  const [ignoreBlur, setIgnoreBlur] = useState(false); // state variable to temporarily ignore the blur event.
  const [showModal, setShowModal] = useState(false);
  const [selectedDesignId, setSelectedDesignId] = useState(null);

  const { search } = useLocation(); // gets the query parameters of the URL and saves them in the variable called 'search'
  const sp = new URLSearchParams(search); // creates a new instance of the URLSearchParams class, passing in the search string, wich allows to apply several methods to work with the query parameters
  const page = sp.get('page') || 1; // Retrieves the value of the 'page' query parameter (the number on wich page the user is on)
  const searchQuery = sp.get('search') || ''; // Retrieves the 'search' parameter from the URL query string; defaults to an empty string if not present
  const filterParam = sp.get('filter') || 'saved';
  const [searchInput, setSearchInput] = useState(searchQuery); // Local state for search input

  const { inFlight: unlikeRequestsInProgress, run: runUnlikeRequest } =
    useInFlightGuard(); // the designs an unlike request is running for
  const [imagesLoading, setImagesLoading] = useState(false);

  const [sort, setSort] = useState('popular'); // Sorting selection
  const [selectedLayout, setSelectedLayout] = useState('all'); // Layout filter
  const [selectedProfile, setSelectedProfile] = useState('all'); // Profile filter
  const [show, setShow] = useState(false);
  const [selectedThumbnail, setSelectedThumbnail] = useState(''); // Store the thumbnail URL
  const [layoutMode, setLayoutMode] = useState(3);
  const [screenSize, setScreenSize] = useState('lg'); // Track current screen size
  const [refreshKey, setRefreshKey] = useState(0); // State to trigger refresh

  function preloadImage(url) {
    return new Promise((resolve) => {
      if (!url) {
        // If there's no URL, resolve immediately to avoid blocking
        return resolve();
      }
      const img = new Image();
      img.src = url;
      img.onload = () => resolve();
      img.onerror = () => resolve();
    });
  }

  useEffect(() => {
    const updateScreenSize = () => {
      if (window.matchMedia('(max-width: 575px)').matches) {
        setScreenSize('xs'); // Extra Small
      } else if (window.matchMedia('(max-width: 767px)').matches) {
        setScreenSize('sm'); // Small
      } else if (window.matchMedia('(max-width: 991px)').matches) {
        setScreenSize('md'); // Medium
      } else {
        setScreenSize('lg'); // Large
      }
    };

    updateScreenSize(); // Set initial size
    window.addEventListener('resize', updateScreenSize); // Update on resize
    return () => window.removeEventListener('resize', updateScreenSize); // Cleanup
  }, []); // Determine screen size dynamically

  useEffect(() => {
    if (screenSize === 'xs' && layoutMode !== 1) {
      setLayoutMode(1);
    } else if (screenSize === 'sm' && layoutMode > 2) {
      setLayoutMode(2);
    } else if (screenSize === 'md' && layoutMode > 3) {
      setLayoutMode(3);
    }
  }, [screenSize, layoutMode]); // Reset layout mode if it's invalid for the current screen size

  const getColClasses = () => {
    switch (layoutMode) {
      case 1:
        return 'col-12';
      case 2:
        return 'col-sm-6';
      case 3:
        return 'col-md-4';
      case 4:
        return 'col-lg-3';
      default:
        return 'col-lg-4 col-md-6 col-sm-12';
    }
  }; // Generate column classes based on layout mode

  useEffect(() => {
    const fetchData = async () => {
      dispatch({ type: 'FETCH_REQUEST' });

      try {
        const { data } = await api.get(
          `/api/designs/my-designs?filter=${filterParam}&page=${page}&search=${encodeURIComponent(
            searchQuery
          )}&sort=${sort}&layout=${selectedLayout}` // encodeURIComponent: encodes the searchQuery to ensure that special characters are properly formatted in the URL, preventing errors and ensuring accurate query handling
        );

        // Check if there are designs before requesting signed URLs
        if (data.designs.length === 0) {
          dispatch({
            type: 'FETCH_SUCCESS',
            payload: { ...data, designs: [] }, // No designs, just return the data
          });
          return;
        }

        // Fetch signed URLs for thumbnails
        const designIds = data.designs.map((design) => design._id);
        const { data: signedUrls } = await api.post('/api/designs/thumbnails', {
          designIds,
        });

        // Merge signed URLs into design metadata
        const updatedDesigns = designsWithThumbnails(
          data.designs,
          signedUrls.thumbnails
        ); // '' for a design the route left out (utils/savedDesigns.js)

        dispatch({
          type: 'FETCH_SUCCESS',
          payload: { ...data, designs: updatedDesigns },
        });
      } catch (err) {
        dispatch({
          type: 'FETCH_FAIL',
          payload: getError(err),
        });
      }
    }; // fetches the saved, liked or pruchased designs for the current user from the backend

    fetchData();
  }, [page, searchQuery, sort, filterParam, selectedLayout, refreshKey]);

  useEffect(() => {
    if (!loading && designs.length > 0) {
      setImagesLoading(true);
      const urls = designs
        .map((d) => d.metadata?.signedThumbnailUrl)
        .filter(Boolean); // ignore empty/null

      Promise.all(urls.map((url) => preloadImage(url)))
        .then(() => setImagesLoading(false))
        .catch((err) => {
          console.error('Error preloading images:', err);
          setImagesLoading(false);
        });
    } else {
      // If still loading data or no designs, set imagesLoading to false
      setImagesLoading(false);
    }
  }, [loading, designs]);

  useEffect(() => {
    const fetchData = async () => {
      dispatch({ type: 'FETCH_LAYOUTS_REQUEST' });

      try {
        const { data } = await api.get(`/api/products/custom-keycap-sets`, {});

        const { products, layouts } = data;

        dispatch({
          type: 'FETCH_LAYOUTS_SUCCESS',
          payload: { products, layouts },
        });
      } catch (err) {
        dispatch({
          type: 'FETCH_LAYOUTS_FAIL',
          payload: getError(err),
        });
      }
    }; // fetches the products and layouts from the backend

    fetchData();
  }, []);

  const submitHandler = (e) => {
    e.preventDefault();
    const sanitizedInput = searchInput.trim(); // Keep spaces between words but remove leading/trailing spaces
    navigate(
      sanitizedInput
        ? `/my-designs?filter=${filterParam}&search=${encodeURIComponent(
            sanitizedInput
          )}&page=1`
        : `/my-designs?filter=${filterParam}&page=1`
    ); // Navigates to the MyDesignsScreen with the search query and resets to page 1 if sanitizedInput exists; otherwise, navigates to page 1 without any search filter
  };

  const deleteHandler = async (design) => {
    if (window.confirm(t('my-designs-screen-delete-confirm'))) {
      try {
        await api.delete(`/api/designs/${design._id}`); // sends a delete request for the selected design to the backend
        toast.success(t('my-designs-screen-design-deleted'));
        setRefreshKey((prevKey) => prevKey + 1); // Increment refreshKey to trigger useEffect
      } catch (err) {
        toast.error(getError(err));
      }
    }
  };

  const handleEditClick = (design) => {
    setEditingId(design._id);
    setName(design.name);
    setTimeout(() => inputRef.current && inputRef.current.focus(), 0);
  };

  const handleChange = (e) => {
    setName(e.target.value);
  };

  const handleNameSubmit = async (design) => {
    const { reason, name: newName } = renameCheck(design, name);

    if (reason === 'not-private') {
      toast.error(t('my-designs-screen-rename-non-private-designs'));
      return;
    }

    setEditingId(null);

    if (reason === 'empty') {
      toast.error(t('my-designs-screen-invalid-name-error'));
      return;
    }

    if (reason === 'unchanged') {
      return null;
    }

    try {
      await api.put(`/api/designs/${design._id}`, { name: newName });
      toast.success(t('my-designs-screen-name-updated'));

      dispatch({
        type: 'UPDATE_DESIGN_NAME',
        payload: {
          designs: designs.map((d) =>
            d._id === design._id ? { ...d, name: newName } : d
          ),
        },
      }); // updating the local state to reflect the change
    } catch (err) {
      toast.error(getError(err));
    }
  };

  const handlePublishing = (designId) => {
    const design = designs.find((d) => d._id === designId);

    if (design.metadata.publicStatus === 'private') {
      // Open confirmation modal for requesting to publish the design
      setSelectedDesignId(designId);
      setShowModal(true);
    } else {
      // Directly toggle the private status without confirmation
      handleConfirmPublish(designId);
    }
  };

  const handleConfirmPublish = async (designId) => {
    const design = designs.find((d) => d._id === designId);

    const change = publicStatusChange(design.metadata.publicStatus); // the status to move to and the message to show, or null for a rejected design

    if (!change) {
      // Case 4: No action for rejected designs
      toast.info(t('my-designs-screen-request-rejected'));
      setSelectedDesignId(null);
      setShowModal(false);
      return;
    }

    // Perform the API request with the appropriate payload
    try {
      await api.put(`/api/designs/${designId}`, {
        publicStatus: change.publicStatus,
      });

      toast.success(t(change.messageKey));

      // Update the local state based on the response
      dispatch({
        type: 'UPDATE_PUBLIC_STATUS',
        payload: {
          id: designId,
          publicStatus: change.publicStatus,
        },
      });
    } catch (err) {
      toast.error(getError(err));
    } finally {
      setSelectedDesignId(null);
      setShowModal(false);
    }
  };

  const handleUnlike = (designId) =>
    runUnlikeRequest(designId, async () => {
      // the hook ignores a design whose unlike request is still running
      try {
        await api.delete('/api/likes', {
          data: { designId },
        });

        // Trigger refresh
        setRefreshKey((prevKey) => prevKey + 1);
      } catch (err) {
        console.error('Error:', err);
        toast.error(getError(err));
      }
    });

  const handleDropdownItemClick = (callback) => {
    setIgnoreBlur(true);
    callback();
    setTimeout(() => setIgnoreBlur(false), 100); // Reset after a short delay
  }; // sets `ignoreBlur` to `true`, calls the callback, and then resets `ignoreBlur` to `false` after a short delay. This prevents the `onBlur` event from interfering with the dropdown item click.

  const handleKeyDown = (event, design) => {
    if (event.key === 'Enter') {
      handleNameSubmit(design);
    }
  };

  const handleShow = (thumbnail) => {
    setSelectedThumbnail(thumbnail); // Update selected thumbnail
    setShow(true); // Show modal
  };
  const handleClose = () => setShow(false);

  const addToCartHandler = (design) => {
    const { reason, item } = savedDesignCartItem(design, products, userInfo);

    if (reason === 'uploaded-icons') {
      toast.error(t('my-designs-screen-design-cannot-be-added-to-cart'));
      return;
    } // prevent adding designs with uploaded icons to the cart unless the signed-in user owns those uploaded icons

    if (reason === 'no-product') {
      toast.error(t('my-designs-screen-product-not-found'));
      return;
    }

    ctxDispatch({
      type: 'CART_ADD_ITEM',
      payload: item,
    });
    toast.success(t('my-designs-screen-added-to-cart'));
  };

  const getProductName = (layout) => productNameOfLayout(products, layout._id); // helper function to get the product name based on a layout (the layout filter maps the layouts list, so the guard for no layout went with the merge into the customizer's helper: a chain 8 review)

  return (
    <Container>
      <title>{t('my-designs-screen-helmet')}</title>

      <h1 className="screen-heading">{t('my-designs-screen-heading')}</h1>

      <Nav
        className="customizer-nav"
        justify
        variant="tabs"
        activeKey={filterParam}
        onSelect={(newFilterKey) => {
          setSearchInput('');
          navigate(`/my-designs?filter=${newFilterKey}&page=1`);
        }}
      >
        <Nav.Item>
          <Nav.Link eventKey="saved">{t('my-designs-screen-saved')}</Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="liked">{t('my-designs-screen-liked')}</Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="purchased">
            {t('my-designs-screen-purchased')}
          </Nav.Link>
        </Nav.Item>
      </Nav>

      {/* Search Form */}
      <Form onSubmit={submitHandler} className="community-designs-search">
        <div style={{ width: '100%' }}>
          <Form.Control
            type="text"
            className="mobile-device-compatible-search"
            placeholder={t('my-designs-screen-search-bar-placeholder')}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            aria-label={t('my-designs-screen-search-bar-placeholder')}
          />
        </div>

        <Button type="submit" variant="secondary">
          {t('my-designs-screen-search')}
        </Button>
      </Form>

      <Row className="community-designs-filters">
        <Col className="d-flex justify-content-between">
          <div className="d-flex align-items-center">
            {/* Sorting dropdown */}
            {filterParam !== 'purchased' ? (
              <Form.Group>
                <Form.Select
                  aria-label="Sort designs"
                  style={{ width: '120px' }}
                  value={sort}
                  onChange={(e) => setSort(e.target.value)}
                >
                  <option value="popular">
                    {t('my-designs-screen-most-liked')}
                  </option>
                  <option value="newest">
                    {t('my-designs-screen-newest')}
                  </option>
                </Form.Select>
              </Form.Group>
            ) : null}

            <DropdownButton
              id="dropdown-basic-button"
              drop="down-centered"
              variant="none"
              className="no-css"
              title={
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  fill="currentColor"
                  className="bi bi-funnel"
                  viewBox="0 0 16 16"
                >
                  <path d="M1.5 1.5A.5.5 0 0 1 2 1h12a.5.5 0 0 1 .5.5v2a.5.5 0 0 1-.128.334L10 8.692V13.5a.5.5 0 0 1-.342.474l-3 1A.5.5 0 0 1 6 14.5V8.692L1.628 3.834A.5.5 0 0 1 1.5 3.5zm1 .5v1.308l4.372 4.858A.5.5 0 0 1 7 8.5v5.306l2-.666V8.5a.5.5 0 0 1 .128-.334L13.5 3.308V2z" />
                </svg>
              }
            >
              <div style={{ padding: '5px 10px' }}>
                <Form.Group className="mb-3">
                  <Form.Label style={{ fontSize: '12px' }}>
                    {t('my-designs-screen-layout')}
                  </Form.Label>

                  <Form.Select
                    aria-label={t('my-designs-screen-layout-filter-lable')}
                    style={{ width: '200px' }}
                    value={selectedLayout}
                    onChange={(e) => setSelectedLayout(e.target.value)}
                  >
                    <option value="all">{t('my-designs-screen-all')}</option>
                    {layouts.map((layout) => (
                      <option key={layout._id} value={layout._id}>
                        {getProductName(layout)}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>

                <Form.Group>
                  <Form.Label style={{ fontSize: '12px' }}>
                    {t('my-designs-screen-profile')}
                  </Form.Label>

                  <Form.Select
                    aria-label={t('my-designs-screen-profile-filter-lable')}
                    style={{ width: '200px' }}
                    value={selectedProfile}
                    onChange={(e) => setSelectedProfile(e.target.value)}
                  >
                    <option value="XDA">XDA</option>
                  </Form.Select>
                </Form.Group>
              </div>
            </DropdownButton>
          </div>

          <div className="d-flex align-items-center">
            {/* Layout Selector */}
            {screenSize !== 'xs' && (
              <Button
                key={1}
                variant={layoutMode === 1 ? 'dark' : 'light'}
                className="me-2"
                onClick={() => setLayoutMode(1)}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="10"
                  height="10"
                  fill="currentColor"
                  className="bi bi-grid"
                  viewBox="0 0 5 5"
                >
                  <rect
                    key={'1'}
                    x={0.5}
                    y={0.5}
                    width="4"
                    height="4"
                    rx="1"
                    fill="currentColor"
                  />
                </svg>
              </Button>
            )}
            {screenSize !== 'xs' && (
              <Button
                key={2}
                variant={layoutMode === 2 ? 'dark' : 'light'}
                className={screenSize === 'sm' ? '' : 'me-2'}
                onClick={() => setLayoutMode(2)}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="10"
                  fill="currentColor"
                  className="bi bi-grid"
                  viewBox="0 0 10 5"
                >
                  <rect
                    key={'1'}
                    x={0.5}
                    y={0.5}
                    width="4"
                    height="4"
                    rx="1"
                    fill="currentColor"
                  />
                  <rect
                    key={'2'}
                    x={5.5}
                    y={0.5}
                    width="4"
                    height="4"
                    rx="1"
                    fill="currentColor"
                  />
                </svg>
              </Button>
            )}
            {screenSize === 'lg' || screenSize === 'md' ? (
              <Button
                key={3}
                variant={layoutMode === 3 ? 'dark' : 'light'}
                className={screenSize === 'md' ? '' : 'me-2'}
                onClick={() => setLayoutMode(3)}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="30"
                  height="10"
                  fill="currentColor"
                  className="bi bi-grid"
                  viewBox="0 0 15 5"
                >
                  <rect
                    key={'1'}
                    x={0.5}
                    y={0.5}
                    width="4"
                    height="4"
                    rx="1"
                    fill="currentColor"
                  />
                  <rect
                    key={'2'}
                    x={5.5}
                    y={0.5}
                    width="4"
                    height="4"
                    rx="1"
                    fill="currentColor"
                  />
                  <rect
                    key={'3'}
                    x={10.5}
                    y={0.5}
                    width="4"
                    height="4"
                    rx="1"
                    fill="currentColor"
                  />
                </svg>
              </Button>
            ) : null}
            {screenSize === 'lg' && (
              <Button
                key={4}
                variant={layoutMode === 4 ? 'dark' : 'light'}
                className={screenSize === 'lg' ? '' : 'me-2'}
                onClick={() => setLayoutMode(4)}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="40"
                  height="10"
                  fill="currentColor"
                  className="bi bi-grid"
                  viewBox="0 0 20 5"
                >
                  <rect
                    key={'1'}
                    x={0.5}
                    y={0.5}
                    width="4"
                    height="4"
                    rx="1"
                    fill="currentColor"
                  />
                  <rect
                    key={'2'}
                    x={5.5}
                    y={0.5}
                    width="4"
                    height="4"
                    rx="1"
                    fill="currentColor"
                  />
                  <rect
                    key={'3'}
                    x={10.5}
                    y={0.5}
                    width="4"
                    height="4"
                    rx="1"
                    fill="currentColor"
                  />
                  <rect
                    key={'4'}
                    x={15.5}
                    y={0.5}
                    width="4"
                    height="4"
                    rx="1"
                    fill="currentColor"
                  />
                </svg>
              </Button>
            )}
          </div>
        </Col>
      </Row>

      {loading || loadingLayouts || imagesLoading ? (
        <div className="overlay">
          <LoadingLogo />
        </div>
      ) : error ? (
        <MessageBox className="danger-alert">{error}</MessageBox>
      ) : (
        <div>
          <Row>
            <Col md={12}>
              <Row>
                {designs.length === 0 && (
                  <div className="w-100">
                    <MessageBox className="default-alert">
                      {filterParam === 'saved'
                        ? t('my-designs-screen-no-saved-designs')
                        : filterParam === 'liked'
                          ? t('my-designs-screen-no-liked-designs')
                          : filterParam === 'purchased'
                            ? t('my-designs-screen-no-purchased-designs')
                            : t('my-designs-screen-no-designs-available')}
                    </MessageBox>
                  </div>
                )}

                {filterParam === 'saved'
                  ? designs.map((design) => {
                      const isSoldOut = isDesignSoldOut(soldOutLayouts, design); // checks if this design's selected layout/profile is sold out

                      return (
                        <Col
                          key={design._id}
                          className={`community-design-card ${getColClasses()}`}
                        >
                          <Card className="design-card">
                            {/* don't apply padding to the Card component, as it will make the imgaes slightly blurry (instead apply it to the Col) */}
                            <Card.Body className={`layout-mode-${layoutMode}`}>
                              <Row className="design-info">
                                <Col className="d-flex align-items-center">
                                  <div className="public-status-badge">
                                    {design.metadata.publicStatus ===
                                    'private' ? (
                                      <Badge
                                        className="design-badge"
                                        bg="secondary"
                                      >
                                        {t('my-designs-screen-private')}
                                      </Badge>
                                    ) : design.metadata.publicStatus ===
                                      'pending' ? (
                                      <Badge
                                        className="design-badge"
                                        bg="secondary"
                                      >
                                        {t('my-designs-screen-pending')}
                                      </Badge>
                                    ) : design.metadata.publicStatus ===
                                      'public' ? (
                                      <Badge
                                        className="design-badge"
                                        bg="success"
                                      >
                                        {t('my-designs-screen-public')}
                                      </Badge>
                                    ) : design.metadata.publicStatus ===
                                      'rejected' ? (
                                      <Badge
                                        className="design-badge"
                                        bg="secondary"
                                      >
                                        {t('my-designs-screen-rejected')}
                                      </Badge>
                                    ) : null}
                                  </div>

                                  <div className="saved-designs-likes-container">
                                    {design.metadata.publicStatus ===
                                    'public' ? (
                                      <div className="d-flex justify-content-evenly align-items-center">
                                        <Card.Text>
                                          <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            className="bi bi-heart saved-design-like-svg"
                                            fill="#333333"
                                            viewBox="0 0 16 16"
                                          >
                                            <path d="m8 2.748-.717-.737C5.6.281 2.514.878 1.4 3.053c-.523 1.023-.641 2.5.314 4.385.92 1.815 2.834 3.989 6.286 6.357 3.452-2.368 5.365-4.542 6.286-6.357.955-1.886.838-3.362.314-4.385C13.486.878 10.4.28 8.717 2.01zM8 15C-7.333 4.868 3.279-3.04 7.824 1.143q.09.083.176.171a3 3 0 0 1 .176-.17C12.72-3.042 23.333 4.867 8 15" />
                                          </svg>

                                          <span className="no-wrap mx-2">
                                            {design.metadata.numOfLikes}
                                          </span>
                                        </Card.Text>
                                      </div>
                                    ) : null}
                                  </div>

                                  <div className="saved-design-dropdown-menu-container">
                                    <DropdownButton
                                      drop="down-centered"
                                      variant="none"
                                      className="no-css exclude-dropdown-arrow"
                                      title={
                                        <svg
                                          xmlns="http://www.w3.org/2000/svg"
                                          fill="#333333"
                                          className="bi bi-three-dots-vertical"
                                          viewBox="0 0 16 16"
                                        >
                                          <path d="M9.5 13a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0m0-5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0m0-5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0" />
                                        </svg>
                                      }
                                      id="dropdown-menu-align-right"
                                    >
                                      <Dropdown.Item
                                        eventKey="1"
                                        onClick={() => {
                                          if (
                                            window.confirm(
                                              t(
                                                'my-designs-screen-design-export-warning'
                                              )
                                            )
                                          ) {
                                            navigate(
                                              `/customizer/${design._id}`
                                            );
                                          }
                                        }}
                                      >
                                        <svg
                                          xmlns="http://www.w3.org/2000/svg"
                                          fill="currentColor"
                                          className="bi bi-grid"
                                          viewBox="0 0 16 16"
                                        >
                                          <path d="M1 2.5A1.5 1.5 0 0 1 2.5 1h3A1.5 1.5 0 0 1 7 2.5v3A1.5 1.5 0 0 1 5.5 7h-3A1.5 1.5 0 0 1 1 5.5zM2.5 2a.5.5 0 0 0-.5.5v3a.5.5 0 0 0 .5.5h3a.5.5 0 0 0 .5-.5v-3a.5.5 0 0 0-.5-.5zm6.5.5A1.5 1.5 0 0 1 10.5 1h3A1.5 1.5 0 0 1 15 2.5v3A1.5 1.5 0 0 1 13.5 7h-3A1.5 1.5 0 0 1 9 5.5zm1.5-.5a.5.5 0 0 0-.5.5v3a.5.5 0 0 0 .5.5h3a.5.5 0 0 0 .5-.5v-3a.5.5 0 0 0-.5-.5zM1 10.5A1.5 1.5 0 0 1 2.5 9h3A1.5 1.5 0 0 1 7 10.5v3A1.5 1.5 0 0 1 5.5 15h-3A1.5 1.5 0 0 1 1 13.5zm1.5-.5a.5.5 0 0 0-.5.5v3a.5.5 0 0 0 .5.5h3a.5.5 0 0 0 .5-.5v-3a.5.5 0 0 0-.5-.5zm6.5.5A1.5 1.5 0 0 1 10.5 9h3a1.5 1.5 0 0 1 1.5 1.5v3a1.5 1.5 0 0 1-1.5 1.5h-3A1.5 1.5 0 0 1 9 13.5zm1.5-.5a.5.5 0 0 0-.5.5v3a.5.5 0 0 0 .5.5h3a.5.5 0 0 0 .5-.5v-3a.5.5 0 0 0-.5-.5z" />
                                        </svg>

                                        {t(
                                          'my-designs-screen-open-in-customizer'
                                        )}
                                      </Dropdown.Item>

                                      <Dropdown.Item
                                        eventKey="2"
                                        onClick={() => addToCartHandler(design)}
                                        disabled={isSoldOut}
                                      >
                                        <svg
                                          xmlns="http://www.w3.org/2000/svg"
                                          fill="currentColor"
                                          className="bi bi-cart"
                                          viewBox="0 0 16 16"
                                        >
                                          <path d="M0 1.5A.5.5 0 0 1 .5 1H2a.5.5 0 0 1 .485.379L2.89 3H14.5a.5.5 0 0 1 .491.592l-1.5 8A.5.5 0 0 1 13 12H4a.5.5 0 0 1-.491-.408L2.01 3.607 1.61 2H.5a.5.5 0 0 1-.5-.5M3.102 4l1.313 7h8.17l1.313-7zM5 12a2 2 0 1 0 0 4 2 2 0 0 0 0-4m7 0a2 2 0 1 0 0 4 2 2 0 0 0 0-4m-7 1a1 1 0 1 1 0 2 1 1 0 0 1 0-2m7 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2" />
                                        </svg>

                                        {isSoldOut
                                          ? t('my-designs-screen-sold-out')
                                          : t('my-designs-screen-add-to-cart')}
                                      </Dropdown.Item>

                                      {design.metadata.publicStatus ===
                                      'private' ? (
                                        <Dropdown.Item
                                          eventKey="3"
                                          disabled={
                                            design.metadata.publicStatus !==
                                            'private'
                                          }
                                          onClick={() =>
                                            handleDropdownItemClick(() =>
                                              handleEditClick(design)
                                            )
                                          }
                                        >
                                          <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            fill="currentColor"
                                            className="bi bi-pen"
                                            viewBox="0 0 16 16"
                                          >
                                            <path d="m13.498.795.149-.149a1.207 1.207 0 1 1 1.707 1.708l-.149.148a1.5 1.5 0 0 1-.059 2.059L4.854 14.854a.5.5 0 0 1-.233.131l-4 1a.5.5 0 0 1-.606-.606l1-4a.5.5 0 0 1 .131-.232l9.642-9.642a.5.5 0 0 0-.642.056L6.854 4.854a.5.5 0 1 1-.708-.708L9.44.854A1.5 1.5 0 0 1 11.5.796a1.5 1.5 0 0 1 1.998-.001m-.644.766a.5.5 0 0 0-.707 0L1.95 11.756l-.764 3.057 3.057-.764L14.44 3.854a.5.5 0 0 0 0-.708z" />
                                          </svg>

                                          {t('my-designs-screen-edit-name')}
                                        </Dropdown.Item>
                                      ) : null}

                                      {design.metadata.publicStatus !==
                                        'rejected' &&
                                      !design.metadata.isCustomLayout ? (
                                        <Dropdown.Item
                                          eventKey="4"
                                          onClick={() => {
                                            handlePublishing(design._id);
                                          }}
                                        >
                                          <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            fill="currentColor"
                                            className="bi bi-share"
                                            viewBox="0 0 16 16"
                                          >
                                            <path d="M13.5 1a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3M11 2.5a2.5 2.5 0 1 1 .603 1.628l-6.718 3.12a2.5 2.5 0 0 1 0 1.504l6.718 3.12a2.5 2.5 0 1 1-.488.876l-6.718-3.12a2.5 2.5 0 1 1 0-3.256l6.718-3.12A2.5 2.5 0 0 1 11 2.5m-8.5 4a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3m11 5.5a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3" />
                                          </svg>

                                          {design.metadata.publicStatus ===
                                          'private'
                                            ? t('my-designs-screen-publish')
                                            : design.metadata.publicStatus ===
                                                'pending'
                                              ? t(
                                                  'my-designs-screen-cancel-request'
                                                )
                                              : design.metadata.publicStatus ===
                                                  'public'
                                                ? t(
                                                    'my-designs-screen-make-private'
                                                  )
                                                : ''}
                                        </Dropdown.Item>
                                      ) : null}

                                      <Dropdown.Item
                                        eventKey="5"
                                        onClick={() => deleteHandler(design)}
                                      >
                                        <svg
                                          xmlns="http://www.w3.org/2000/svg"
                                          fill="currentColor"
                                          className="bi bi-trash"
                                          viewBox="0 0 16 16"
                                        >
                                          <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5m2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5m3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0z" />
                                          <path d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4zM2.5 3h11V2h-11z" />
                                        </svg>

                                        {t('my-designs-screen-delete')}
                                      </Dropdown.Item>
                                    </DropdownButton>
                                  </div>
                                </Col>
                              </Row>

                              <DesignThumbnail
                                design={design}
                                layoutMode={layoutMode}
                                isZoomable={
                                  screenSize !== 'xs' && layoutMode !== 1
                                }
                                onZoom={handleShow}
                              />

                              <div>
                                <div className="design-card-divider"></div>

                                <div className="saved-design-name">
                                  {editingId === design._id ? (
                                    <input
                                      className="mobile-device-compatible form-control"
                                      ref={inputRef}
                                      type="text"
                                      value={name}
                                      onChange={handleChange}
                                      onBlur={() =>
                                        !ignoreBlur && handleNameSubmit(design)
                                      }
                                      onKeyDown={(event) =>
                                        handleKeyDown(event, design)
                                      }
                                      maxLength="55"
                                      disabled={
                                        design.metadata.publicStatus !==
                                        'private'
                                      }
                                    />
                                  ) : design.metadata.publicStatus ===
                                    'private' ? (
                                    // only the owner's private designs can be renamed, so only then is the name a button:
                                    <button
                                      type="button"
                                      className="a11y-unstyled-button saved-design-name-button"
                                      onClick={() => handleEditClick(design)}
                                    >
                                      {design.name}
                                    </button>
                                  ) : (
                                    design.name
                                  )}
                                </div>
                              </div>
                            </Card.Body>
                          </Card>
                        </Col>
                      );
                    })
                  : filterParam === 'liked'
                    ? designs.map((design) => {
                        const isSoldOut = isDesignSoldOut(
                          soldOutLayouts,
                          design
                        ); // checks if this design's selected layout/profile is sold out

                        const isPurchaseDisabled = purchaseDisabled(
                          design,
                          userInfo
                        ); // disables purchase for other users if this design contains visible user-uploaded icons (utils/savedDesigns.js, the cart item's rule; a copy here until chain 10, Oct 2026)

                        return (
                          <Col
                            key={design._id}
                            className={`community-design-card ${getColClasses()}`}
                          >
                            <Card className="design-card">
                              {/* don't apply padding to the Card component, as it will make the imgaes slightly blurry (instead apply it to the Col) */}
                              <Card.Body
                                className={`layout-mode-${layoutMode}`}
                              >
                                <Row className="design-info">
                                  <Col className="d-flex justify-content-between align-items-center">
                                    <div className="design-name">
                                      {design.name}
                                      <span className="username">
                                        {t('community-designs-screen-by')}{' '}
                                        {design.metadata.username}
                                      </span>
                                    </div>

                                    <div className="likes">
                                      <Button
                                        variant="none"
                                        onClick={() => handleUnlike(design._id)}
                                        disabled={
                                          !design ||
                                          unlikeRequestsInProgress.has(
                                            design._id
                                          )
                                        } // Disable if design is null or a request for this design is in progress
                                        className="no-css like-button-reset"
                                      >
                                        <div className="d-flex justify-content-evenly align-items-center">
                                          <Card.Text>
                                            <svg
                                              xmlns="http://www.w3.org/2000/svg"
                                              fill="#fe4a49"
                                              className="bi bi-heart-fill design-like-svg"
                                              viewBox="0 0 16 16"
                                            >
                                              <path
                                                fillRule="evenodd"
                                                d="M8 1.314C12.438-3.248 23.534 4.735 8 15-7.534 4.736 3.562-3.248 8 1.314"
                                              />
                                            </svg>

                                            <span className="no-wrap mx-2 liked-count">
                                              {design.metadata.numOfLikes}
                                            </span>
                                          </Card.Text>
                                        </div>
                                      </Button>
                                    </div>
                                  </Col>
                                </Row>

                                <DesignThumbnail
                                  design={design}
                                  layoutMode={layoutMode}
                                  isZoomable={
                                    screenSize !== 'xs' && layoutMode !== 1
                                  }
                                  onZoom={handleShow}
                                />

                                <div>
                                  <div className="design-card-divider"></div>

                                  <div className="d-flex justify-content-center">
                                    <Button
                                      variant="light"
                                      className={`add-to-cart-button ${
                                        isPurchaseDisabled
                                          ? 'invisible-button-space'
                                          : ''
                                      }`}
                                      onClick={() => addToCartHandler(design)}
                                      disabled={isSoldOut || isPurchaseDisabled}
                                    >
                                      {isSoldOut
                                        ? t('my-designs-screen-sold-out')
                                        : t('my-designs-screen-add-to-cart')}
                                    </Button>
                                  </div>
                                </div>
                              </Card.Body>
                            </Card>
                          </Col>
                        );
                      })
                    : filterParam === 'purchased'
                      ? designs.map((design) => {
                          const isDesignOwner = ownsDesign(userInfo, design); // checks if the signed-in user is the creator/owner of this design (utils/savedDesigns.js)

                          const isSoldOut = isDesignSoldOut(
                            soldOutLayouts,
                            design
                          ); // checks if this design's selected layout/profile is sold out

                          const isDeletedAndNotOwner =
                            !isDesignOwner && design.metadata.deleted; // disables if the design is deleted and doesn't belong to the user

                          const isNotPublicAndNotOwner =
                            !isDesignOwner &&
                            design.metadata.publicStatus !== 'public'; // disables if the design isn't public and doesn't belong to the user

                          const isPurchaseDisabled = purchaseDisabled(
                            design,
                            userInfo
                          ); // disables purchase for other users if this design contains visible user-uploaded icons (utils/savedDesigns.js, the cart item's rule; a copy here until chain 10, Oct 2026)

                          const isNoLongerAvailable =
                            isDeletedAndNotOwner || isNotPublicAndNotOwner; // the reasons the user gets explained in a toast, rather than just a dead button

                          return (
                            <Col
                              key={design._id}
                              className={`community-design-card ${getColClasses()}`}
                            >
                              <Card className="design-card">
                                {/* don't apply padding to the Card component, as it will make the imgaes slightly blurry (instead apply it to the Col) */}
                                <Card.Body
                                  className={`layout-mode-${layoutMode}`}
                                >
                                  <Row className="design-info">
                                    <Col className="d-flex justify-content-between align-items-center">
                                      <div className="design-name">
                                        {design.name}
                                        {design.metadata.username !==
                                          userInfo.username && (
                                          <span className="username">
                                            {t('community-designs-screen-by')}{' '}
                                            {design.metadata.username}
                                          </span>
                                        )}
                                      </div>
                                    </Col>
                                  </Row>

                                  <DesignThumbnail
                                    design={design}
                                    layoutMode={layoutMode}
                                    isZoomable={
                                      screenSize !== 'xs' && layoutMode !== 1
                                    }
                                    onZoom={handleShow}
                                  />

                                  <div>
                                    <div className="design-card-divider"></div>

                                    <div className="d-flex justify-content-center">
                                      <Button
                                        variant="light"
                                        className={`add-to-cart-button${
                                          isNoLongerAvailable
                                            ? ' a11y-looks-disabled'
                                            : ''
                                        }`}
                                        onClick={() => {
                                          if (isNoLongerAvailable) {
                                            toast.error(
                                              t(
                                                'my-designs-screen-design-no-longer-available'
                                              )
                                            );
                                            return;
                                          }

                                          addToCartHandler(design);
                                        }}
                                        // a design that is gone stays clickable (only greyed out via aria-disabled) so the explaining toast is reachable by mouse and keyboard — the other reasons disable the button outright, as before:
                                        aria-disabled={
                                          isNoLongerAvailable || undefined
                                        }
                                        disabled={
                                          isSoldOut || isPurchaseDisabled
                                        }
                                      >
                                        {isSoldOut
                                          ? t('my-designs-screen-sold-out')
                                          : t('my-designs-screen-buy-again')}
                                      </Button>
                                    </div>
                                  </div>
                                </Card.Body>
                              </Card>
                            </Col>
                          );
                        })
                      : null}

                {/* Confirmation Modal */}
                <Modal
                  className="publish-design-modal"
                  show={showModal}
                  onHide={() => {
                    setSelectedDesignId(null);
                    setShowModal(false);
                  }}
                >
                  <Modal.Header closeButton>
                    <Modal.Title>
                      {t('my-designs-screen-confirm-publish-modal-title')}
                    </Modal.Title>
                  </Modal.Header>

                  <Modal.Body>
                    <p>
                      {t('my-designs-screen-confirm-publish-modal-content-1')}{' '}
                      <strong>
                        {t('my-designs-screen-confirm-publish-modal-content-2')}
                      </strong>{' '}
                      {t('my-designs-screen-confirm-publish-modal-content-3')}
                    </p>

                    <p>
                      {t('my-designs-screen-confirm-publish-modal-content-4')}{' '}
                      <strong>
                        {t('my-designs-screen-confirm-publish-modal-content-5')}
                      </strong>{' '}
                      {t('my-designs-screen-confirm-publish-modal-content-6')}
                    </p>

                    <p>
                      {t('my-designs-screen-confirm-publish-modal-content-7')}
                      <ul>
                        <li>
                          {t(
                            'my-designs-screen-confirm-publish-modal-content-8'
                          )}
                        </li>
                        <li>
                          {t(
                            'my-designs-screen-confirm-publish-modal-content-9'
                          )}
                        </li>
                        <li>
                          {t(
                            'my-designs-screen-confirm-publish-modal-content-10'
                          )}
                        </li>
                        <li>
                          {t(
                            'my-designs-screen-confirm-publish-modal-content-11'
                          )}
                        </li>
                        <li>
                          {t(
                            'my-designs-screen-confirm-publish-modal-content-12'
                          )}
                        </li>
                      </ul>
                    </p>

                    <p>
                      {t('my-designs-screen-confirm-publish-modal-content-13')}
                    </p>

                    <p>
                      {t('my-designs-screen-confirm-publish-modal-content-14')}
                    </p>
                  </Modal.Body>

                  <Modal.Footer>
                    <Button
                      variant="secondary"
                      onClick={() => {
                        setSelectedDesignId(null);
                        setShowModal(false);
                      }}
                    >
                      {t('my-designs-screen-cancel')}
                    </Button>

                    <Button
                      variant="primary"
                      onClick={() => handleConfirmPublish(selectedDesignId)}
                    >
                      {t('my-designs-screen-confirm')}
                    </Button>
                  </Modal.Footer>
                </Modal>

                <Modal
                  show={show}
                  onHide={handleClose}
                  centered
                  className="thumbnail-modal"
                >
                  <Modal.Body>
                    <img
                      src={selectedThumbnail}
                      alt={t('my-designs-screen-selected-thumbnail')}
                    />
                  </Modal.Body>

                  <Modal.Footer>
                    <Button variant="secondary" onClick={handleClose}>
                      {t('my-designs-screen-close')}
                    </Button>
                  </Modal.Footer>
                </Modal>

                <Pagination
                  pages={pages}
                  page={Number(page)}
                  filterParam={filterParam}
                  baseUrl="/my-designs"
                  search={searchQuery}
                />
              </Row>
            </Col>
          </Row>
        </div>
      )}
    </Container>
  );
}
